/** **************************************************************************************
 * Copyright (c) 2023. Peter Lemke, SV-Studios                                          *
 * This File, its contents and by extension the corresponding project is property of    *
 * SV-Studios and may not be used without explicit permission to do so.                 *
 *                                                                                      *
 * management@sv-studios.net                                                            *
 *************************************************************************************** */

import React from "react";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import Switch from "@mui/material/Switch";
import MDTypography from "../MDTypography";
import MDBox from "../MDBox";
import MDButton from "../MDButton";
import { useDispatch, useSelector } from "react-redux";
import { cookieActions } from "../../store/cookie/cookie-slice";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";

export default function CookieDialog() {
  const {
    analytics: statisticsCookie,
    preferences: preferencesCookie,
    showDialog,
  } = useSelector((state) => state.cookie);
  const dispatch = useDispatch();

  const [statistics, setStatistics] = React.useState(statisticsCookie || false);
  const [preferences, setPreferences] = React.useState(preferencesCookie || false);

  const handleClose = () => {
    dispatch(cookieActions.setShowDialog(false));
  };

  const accept = (type) => {
    switch (type) {
      case "required":
        dispatch(cookieActions.setAnalytics(false));
        dispatch(cookieActions.setPreferences(false));
        break;
      case "selected":
        dispatch(cookieActions.setAnalytics(statistics));
        dispatch(cookieActions.setPreferences(preferences));
        break;
      case "all":
        dispatch(cookieActions.setAnalytics(true));
        dispatch(cookieActions.setPreferences(true));
        break;
    }
    dispatch(cookieActions.setRequired(true));
    handleClose();
  };

  return (
    <>
      <Dialog
        open={showDialog}
        maxWidth="lg"
        fullWidth={true}
        className=" dark:bg-panel-800 dark:shadow-panel-900"
        handler={handleClose}
        dismiss={{
          ancestorScroll: false,
          outsidePointerDown: false,
          referencePointerDown: false,
          bubbles: false,
          escapeKey: false,
          enabled: false,
        }}
      >
        <DialogTitle className="dark:text-gray-200">Cookies</DialogTitle>
        <DialogContent divider className="grid grid-cols-3 dark:border-y-panel-500">
          <div className="col-span-2">
            <MDTypography
              className="font-normal dark:text-gray-400"
              variant="body2"
              fontWeight="regular"
              gutterBottom
            >
              Wir verwenden Cookies, um Ihnen ein angenehmeres Surfen zu ermöglichen. Durch die Nutzung
              unserer Website stimmen Sie der Verwendung von Cookies zu. Wir empfehlen Ihnen die Cookies zu
              akzeptieren, da wir durch Statistiken und Fehlertracking unsere Dienste für Sie verbessern
              können. Sie können Ihre Cookie-Einstellungen jederzeit ändern. <br />
            </MDTypography>
            <MDTypography variant="body2">
              Weitere Informationen finden Sie in unserer{" "}
              <Link
                underline="hover"
                href="https://zyonic.de/datenschutz"
                target="_blank"
                rel="noopener noreferrer"
              >
                Datenschutzerklärung
              </Link>
            </MDTypography>
          </div>
          <Divider />
          <div className="flex flex-col gap-1">
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3} lineHeight={1}>
              <MDTypography variant="h6">Notwendige Cookies</MDTypography>
              <Switch checked readOnly />
            </MDBox>
            <MDTypography variant="body2">
              Erforderliche Cookies sind unverzichtbar, damit meine Website ordnungsgemäß funktioniert. Diese
              Cookies ermöglichen grundlegende Funktionen wie die Navigation auf der Website und den Zugang zu
              sicheren Bereichen. Sie speichern keine persönlichen Daten und stellen sicher, dass Sie alle
              wesentlichen Dienste dieser Website nutzen können.
            </MDTypography>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
              <MDTypography variant="h6">Analyse</MDTypography>
              <Switch checked={statistics} onChange={() => setStatistics(!statistics)} />
            </MDBox>
            <MDTypography variant="body2">
              Analyse-Cookies helfen uns zu verstehen, wie Sie unsere Website nutzen. Sie sammeln anonyme
              Informationen über Ihre Besuche und Interaktionen, sodass wird die Leistung der Website
              verbessern und relevantere Inhalte bereitstellen kann.
            </MDTypography>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
              <MDTypography variant="h6">Präferenzen</MDTypography>
              <Switch checked={preferences} onChange={() => setPreferences(!preferences)} />
            </MDBox>
            <MDTypography variant="body2">
              Präferenz-Cookies speichern Ihre Einstellungen und Präferenzen, damit unsere Website optimal auf
              Ihre Bedürfnisse zugeschnitten ist. Sie erinnern sich z.B. an Ihre bevorzugte Sprache und
              Design, in der Sie sich befinden.
            </MDTypography>
          </div>
          <Divider />
          <MDTypography variant="body2">
            Wenn Sie Cookies deaktivieren, nachdem diese bereits gesetzt wurden, empfehlen wir die Seite neu
            zu laden, um sicherzustellen, dass die Änderungen wirksam werden.
          </MDTypography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // für kleine Bildschirme (xs und kleiner)
              sm: "1fr 1fr", // für mittlere Bildschirme (sm)
              lg: "1fr 1fr 1fr", // für große Bildschirme (lg und größer)
            },
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <MDButton
            variant="gradient"
            color="secondary"
            onClick={() => accept("required")}
            sx={{
              width: "100%",
              height: "100%",
              marginLeft: "0 !important",
            }}
          >
            <span>Nur Notwendige Akzeptieren</span>
          </MDButton>
          <MDButton
            variant="gradient"
            color="secondary"
            onClick={() => accept("selected")}
            sx={{
              width: "100%",
              height: "100%",
              marginLeft: "0 !important",
            }}
          >
            <span>Alle Ausgewählten Akzeptieren</span>
          </MDButton>
          <MDButton
            variant="gradient"
            color="secondary"
            id="allButton"
            onClick={() => accept("all")}
            sx={{
              width: "100%",
              height: "100%",
              gridColumn: { sm: "span 2", lg: "auto" },
              marginLeft: "0 !important",
            }}
          >
            <span>Alle Akzeptieren</span>
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
